/**
 * Mockup→Live Phase 3 — Zeitleiste (Timeline-Widget).
 *
 * Portierung des Mockup-Timelines (`MockupStep3Page.jsx` v3) auf reale
 * VordienstZeile-Daten. Zeigt jede Beschäftigungszeit als horizontalen
 * Balken auf einer Jahresachse; überlappende Zeiten werden in eigenen
 * „Lanes" gestapelt.
 *
 * Balken-Farb-Semantik (identisch zum Mockup):
 *   • emerald  — KV-Regel greift (Provenance = kv-rule / kv-praktikum)
 *   • brand blau + Diagonal-Muster — Annahme / Manuell (User- oder Fallback-Faktor)
 *   • warm-grau — nicht anrechenbar
 *   • gedämpft (opacity 25 %) — Anteil NACH der Höchstanrechnungsdauer
 *
 * Grenz-Linie: sky-blau gestrichelte Vertikale + Label „Höchstanrechnungs-
 * dauer erreicht · DD.MM.YYYY", sichtbar nur wenn `capReachedDate` bekannt.
 *
 * PROPS:
 *   rows           — Vordienst-Rows (`row.datum_von/bis`, `anrechenbar`,
 *                    `anrechnungsfaktor`, `_factor_manual`, `_kv_rule_applied`,
 *                    `_cap_reduced`, `effective_days_anrechenbar`, `taetigkeit`,
 *                    `arbeitgeber`, `was_laufend`)
 *   capMonths      — KV-Cap in Monaten (z.B. 84 = 7 Jahre)
 *   stichtag       — ISO-String YYYY-MM-DD, Fallback für `was_laufend`-Zeilen
 *   requiresEquivalenceCheck / faktorRegeln — für Provenance-Klassifikation
 */
import React, { useMemo } from "react";
import { ChartLine } from "@phosphor-icons/react";
import { classifyFactorProvenance, isPraktikumPosition } from "../shared";

// ISO YYYY-MM-DD → Date (Zeit auf 00:00 UTC-neutral)
function parseIso(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

// ISO YYYY-MM-DD → „DD.MM.YYYY" für Anzeige
function isoToGermanShort(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

// Berechnet grob die anrechenbaren Tage einer Zeile für Cap-Kumulation.
// Nutzt backend-provided `effective_days_anrechenbar` wenn vorhanden,
// sonst fällt auf Kalendertage × Faktor zurück (30/360-Konvention hier
// weggelassen — für die Timeline-Annäherung ausreichend).
function estimateAnrechenbareTage(row, stichtag) {
  if (typeof row.effective_days_anrechenbar === "number") {
    return row.effective_days_anrechenbar;
  }
  const start = parseIso(row.datum_von);
  const end = row.was_laufend ? parseIso(stichtag) : parseIso(row.datum_bis);
  if (!start || !end) return 0;
  const rawDays = Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)));
  const faktor = typeof row.anrechnungsfaktor === "number" ? row.anrechnungsfaktor : 1;
  return Math.round(rawDays * faktor);
}

/**
 * Ermittelt approximativ das Datum, an dem die Höchstanrechnungsdauer
 * erreicht wurde. Iteriert chronologisch über alle anrechenbaren
 * Positionen und findet die Zeile, in der die kumulierte anrechenbare
 * Dauer den KV-Cap überschreitet — plus den anteiligen Datumsversatz
 * innerhalb dieser Zeile.
 * @returns {string|null} ISO YYYY-MM-DD oder null wenn Cap nicht erreicht
 */
function computeCapReachedIso(rows, capMonths, stichtag) {
  if (!capMonths || capMonths <= 0) return null;
  const capDays = capMonths * 30; // 30/360-Konvention (Backend-kompatibel)
  const eligible = rows
    .filter((r) => r.anrechenbar && r.datum_von && (r.datum_bis || r.was_laufend))
    .map((r) => ({
      row: r,
      startMs: parseIso(r.datum_von)?.getTime() || 0,
      endMs: (r.was_laufend
        ? parseIso(stichtag)?.getTime()
        : parseIso(r.datum_bis)?.getTime()) || 0,
      anrechenbareTage: estimateAnrechenbareTage(r, stichtag),
    }))
    .filter((e) => e.startMs && e.endMs && e.anrechenbareTage > 0)
    .sort((a, b) => a.startMs - b.startMs);

  let cum = 0;
  for (const e of eligible) {
    if (cum + e.anrechenbareTage >= capDays) {
      const remaining = capDays - cum;
      const frac = Math.min(1, Math.max(0, remaining / e.anrechenbareTage));
      const reachedMs = e.startMs + frac * (e.endMs - e.startMs);
      const d = new Date(reachedMs);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
    cum += e.anrechenbareTage;
  }
  return null;
}

export default function Timeline({
  rows,
  capMonths,
  stichtag,
  requiresEquivalenceCheck = false,
  faktorRegeln = {},
}) {
  // -----------------------------------------------------------------
  // Datenaufbereitung
  //
  // WICHTIG: `rowIdx` = Position in der ORIGINALEN `rows`-Array-Reihenfolge.
  // Wird für Klick-Scroll auf `#vd-row-anchor-{rowIdx}` gebraucht — der
  // Anker steht auf VordienstRow. Filter (`.filter(Boolean)`) erhält
  // diese Original-Indexierung, weil wir mit `rows.map((r, idx) => ...)`
  // arbeiten (nicht mit einer bereits gefilterten Kopie).
  // -----------------------------------------------------------------
  const items = useMemo(() => {
    return (rows || [])
      .map((r, idx) => {
        if (!r.datum_von || (!r.datum_bis && !r.was_laufend)) return null;
        const startD = parseIso(r.datum_von);
        const endD = r.was_laufend ? parseIso(stichtag) : parseIso(r.datum_bis);
        if (!startD || !endD) return null;
        const prov = classifyFactorProvenance(r, faktorRegeln, {
          equivalenceCheckRequired: requiresEquivalenceCheck,
        });
        // Iter 20.03.P5-fix — Punkt 1 aus User-Report Martina Fall 4.
        // Praktikum-Zeilen OHNE Anstellungstyp-Wahl sind noch nicht
        // klassifiziert (User muss Angestellter/Arbeiter wählen). Sie
        // gehören semantisch in die „Annahme"-Kategorie (blau gestreift),
        // NICHT in „Manuell" (amber). `classifyFactorProvenance` gibt
        // in diesem Fall `"n/a"` zurück — das Timeline-Rendering fiele
        // in den Fallback-Ast `bg-brand-300` ohne Streifen. Wir mappen
        // Praktikum-offen explizit auf `isAnnahme=true`, damit die
        // Timeline die gleiche gestreifte Optik zeigt wie die anderen
        // ungeprüften Zeilen.
        const isPraktikumOffen =
          isPraktikumPosition(r.taetigkeit) && !r.praktikum_anstellungstyp;
        return {
          id: `row-${idx}`,
          rowIdx: idx,
          startMs: startD.getTime(),
          endMs: endD.getTime(),
          startIso: r.datum_von,
          endIso: r.was_laufend ? stichtag : r.datum_bis,
          anrechenbar: !!r.anrechenbar,
          isKvRule: isPraktikumOffen
            ? false
            : (prov === "kv-rule" || prov === "kv-praktikum"),
          isManual: isPraktikumOffen ? false : (prov === "manual"),
          isAnnahme: isPraktikumOffen ? true : (prov === "default"),
          isCapReduced: r._cap_reduced === true,
          isCapZeroed: r._cap_zeroed === true,
          taetigkeit: r.taetigkeit || "",
          arbeitgeber: r.arbeitgeber || "",
          wasLaufend: !!r.was_laufend,
        };
      })
      .filter(Boolean);
  }, [rows, stichtag, requiresEquivalenceCheck, faktorRegeln]);

  const capReachedIso = useMemo(
    () => computeCapReachedIso(rows || [], capMonths, stichtag),
    [rows, capMonths, stichtag],
  );

  if (items.length === 0) {
    return (
      <div
        className="rounded-sm border border-ink-200 bg-white p-4 text-sm text-warm-500"
        data-testid="tool-step3-timeline-empty"
      >
        Keine Zeitleisten-Daten — Von- und Bis-Datum sind auf allen Positionen erforderlich.
      </div>
    );
  }

  // -----------------------------------------------------------------
  // Achsen-Range
  // -----------------------------------------------------------------
  const yStartMs = Math.min(...items.map((i) => i.startMs));
  const yEndMs = Math.max(...items.map((i) => i.endMs));
  const yStart = new Date(yStartMs).getFullYear();
  const yEnd = new Date(yEndMs).getFullYear();
  const span = Math.max(1, yEnd - yStart);
  const tickCount = Math.min(8, span + 1);
  const rangeStartMs = new Date(yStart, 0, 1).getTime();
  const rangeEndMs = new Date(yEnd, 11, 31).getTime();
  const totalMs = Math.max(1, rangeEndMs - rangeStartMs);

  // -----------------------------------------------------------------
  // Lane-Assignment (gestapelte Balken bei zeitlicher Überlappung)
  // -----------------------------------------------------------------
  const sorted = [...items].sort((a, b) => a.startMs - b.startMs);
  const lanes = [];
  const positionLane = new Map();
  for (const p of sorted) {
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      if (lanes[i] < p.startMs) {
        lanes[i] = p.endMs;
        positionLane.set(p.id, i);
        placed = true;
        break;
      }
    }
    if (!placed) {
      lanes.push(p.endMs);
      positionLane.set(p.id, lanes.length - 1);
    }
  }
  const laneCount = Math.max(1, lanes.length);
  const laneH = 10;
  const laneGap = 3;
  const chartH = laneCount * (laneH + laneGap) + 8;

  const capMs = capReachedIso ? parseIso(capReachedIso)?.getTime() : null;
  const capLeft =
    capMs != null && capMs >= rangeStartMs && capMs <= rangeEndMs
      ? ((capMs - rangeStartMs) / totalMs) * 100
      : null;

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------
  return (
    <div
      className="rounded-sm border border-ink-200 bg-white p-4 pb-14"
      data-testid="tool-step3-timeline"
    >
      <div className="mb-2 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-700">
          <ChartLine size={14} weight="duotone" className="text-brand-500" />
          Zeitleiste ({yStart} – {yEnd})
        </div>
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-ink-600">
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-3 rounded-[1px] bg-emerald-500" /> Anrechenbar
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-3 rounded-[1px] bg-amber-400 border border-amber-500" /> Manuell
          </span>
          <span className="inline-flex items-center gap-1">
            <span
              className="h-2 w-3 rounded-[1px] border border-brand-500"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(45deg,#93c5fd,#93c5fd 2px,#dbeafe 2px,#dbeafe 4px)",
              }}
            />
            Annahme
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-3 rounded-[1px] bg-ink-300" /> nicht anrechenbar
          </span>
          {capReachedIso && (
            <span className="inline-flex items-center gap-1">
              <span className="h-2 w-3 rounded-[1px] bg-emerald-500 opacity-25" />
              nach Höchstanrechnungsdauer
            </span>
          )}
        </div>
      </div>
      <div
        className="relative rounded-sm bg-ink-50/70"
        style={{ height: `${chartH}px` }}
      >
        {/* Jahres-Grid */}
        {Array.from({ length: tickCount }).map((_, i) => {
          const y = yStart + Math.round((i * span) / (tickCount - 1));
          const leftPct = (i * 100) / (tickCount - 1);
          return (
            <div
              key={i}
              className="absolute top-0 h-full"
              style={{ left: `${leftPct}%` }}
            >
              <div className="h-full w-px bg-ink-200/70" />
              <span className="absolute -bottom-4 -translate-x-1/2 text-[10px] text-ink-500 tabular-nums">
                {y}
              </span>
            </div>
          );
        })}

        {/* Cap-Grenz-Linie */}
        {capLeft != null && (
          <>
            <div
              className="pointer-events-none absolute top-0 z-10"
              style={{ left: `${capLeft}%`, height: `${chartH + 44}px` }}
              data-testid="tool-step3-timeline-cap-line"
            >
              <div className="h-full w-px border-l border-dashed border-sky-500/70" />
            </div>
            <div
              className={[
                "pointer-events-none absolute z-10 whitespace-nowrap rounded-sm border border-sky-300 bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-800",
                capLeft > 55 ? "-translate-x-full" : "translate-x-1",
              ].join(" ")}
              style={{ left: `${capLeft}%`, bottom: "-2.75rem" }}
              data-testid="tool-step3-timeline-cap-label"
            >
              Höchstanrechnungsdauer erreicht · {isoToGermanShort(capReachedIso)}
            </div>
          </>
        )}

        {/* Position-Balken */}
        {sorted.map((p) => {
          const left = ((p.startMs - rangeStartMs) / totalMs) * 100;
          const width = Math.max(
            0.25,
            ((p.endMs - p.startMs) / totalMs) * 100,
          );
          const lane = positionLane.get(p.id) || 0;

          const isAfterCap =
            capMs != null && p.anrechenbar && p.startMs > capMs;
          const isCrossingCap =
            capMs != null &&
            p.anrechenbar &&
            p.startMs <= capMs &&
            p.endMs > capMs;

          const barBg = !p.anrechenbar
            ? "bg-ink-300"
            : p.isKvRule
              ? "bg-emerald-500"
              : p.isManual
                ? "bg-amber-400"
                : "bg-brand-300";
          const barBorder = !p.anrechenbar
            ? "border-ink-400"
            : p.isKvRule
              ? "border-emerald-600"
              : p.isManual
                ? "border-amber-500"
                : "border-brand-500";
          // Streifen-Muster nur für „Annahme" (Default) — Manuell bleibt
          // sauber ausgefüllt amber, weil der User bewusst gesetzt hat.
          const barStyle = p.anrechenbar && p.isAnnahme
            ? {
                backgroundImage:
                  "repeating-linear-gradient(45deg,#93c5fd,#93c5fd 4px,#dbeafe 4px,#dbeafe 8px)",
              }
            : undefined;

          const scrollToRow = () => {
            const el = document.getElementById(`vd-row-anchor-${p.rowIdx}`);
            if (!el || typeof el.scrollIntoView !== "function") return;
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            // Iter 20.02.P5-fix — Ziel-Zeile für 2 Sekunden mit sanftem
            // Amber-Ring highlighten (siehe .vd-row-flash-Keyframe in
            // index.css). Ohne den Highlight müsste der User in einer
            // 20-Zeilen-Liste selber suchen, welche Zeile gerade
            // angesprungen wurde.
            const rowEl = el.closest("li") || el.parentElement;
            if (rowEl && rowEl.classList) {
              // Duplikate vermeiden: erst entfernen (falls User schnell
              // hintereinander klickt) und beim nächsten Frame wieder
              // hinzufügen, damit die Animation neu startet.
              rowEl.classList.remove("vd-row-flash");
              void rowEl.offsetWidth; // Force reflow → Animation-Restart
              rowEl.classList.add("vd-row-flash");
              // 3,5 s + kleine Karenz — matcht die Animation-Dauer in
              // index.css. Sicherstellt, dass die Klasse auch bei
              // Multi-Click sauber entfernt wird (sonst würde ein
              // zweiter Klick den Timer der ersten Klasse triggern
              // und die zweite Animation vorzeitig abbrechen).
              window.setTimeout(() => {
                if (rowEl.classList) rowEl.classList.remove("vd-row-flash");
              }, 3700);
            }
          };

          return (
            <button
              key={p.id}
              type="button"
              onClick={scrollToRow}
              className={[
                "absolute rounded-[2px] border transition-crisp cursor-pointer hover:brightness-110 hover:z-20 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400",
                barBg,
                barBorder,
                isAfterCap ? "opacity-25" : "",
              ].join(" ")}
              style={{
                left: `${left}%`,
                width: `${width}%`,
                top: `${6 + lane * (laneH + laneGap)}px`,
                height: `${laneH}px`,
                ...barStyle,
              }}
              title={[
                p.taetigkeit || "(keine Tätigkeit)",
                p.arbeitgeber ? `@ ${p.arbeitgeber}` : "",
                `(${isoToGermanShort(p.startIso)} – ${p.wasLaufend ? "laufend" : isoToGermanShort(p.endIso)})`,
                isAfterCap ? "· nach Höchstanrechnungsdauer" : "",
                isCrossingCap ? "· reicht über die Höchstanrechnungsdauer" : "",
                "— zur Zeile springen",
              ]
                .filter(Boolean)
                .join(" ")}
              aria-label={`Zu Zeile ${p.rowIdx + 1} springen: ${p.taetigkeit || "(keine Tätigkeit)"} von ${isoToGermanShort(p.startIso)} bis ${p.wasLaufend ? "laufend" : isoToGermanShort(p.endIso)}`}
              data-testid={`tool-step3-timeline-bar-${p.id}`}
              data-row-index={p.rowIdx}
            >
              {isCrossingCap && (
                <span
                  aria-hidden="true"
                  className={[
                    "absolute inset-y-0 rounded-r-[2px] opacity-25 pointer-events-none",
                    barBg,
                    barBorder,
                    "border",
                  ].join(" ")}
                  style={{
                    left: `${((capMs - p.startMs) / (p.endMs - p.startMs)) * 100}%`,
                    right: 0,
                    ...barStyle,
                  }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
